import { Component, Input, OnInit, Pipe, PipeTransform } from '@angular/core';
import { Lightbox } from 'ngx-lightbox';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProductAsset from 'src/app/model/product-asset';
import Board from 'src/app/model/product/board/board';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import SecondBoard from 'src/app/model/product/ip-board/second-board';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import Product from 'src/app/model/product/product';
import { CLIENT_WINDOW_STICKER_TYPE } from 'src/app/model/product/windowsticker/client-windowsticker';
import CustomWindowsticker from 'src/app/model/product/windowsticker/custom-windowsticker';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import Address from 'src/app/model/project/address';
import { Adjustment } from 'src/app/model/project/adjustment';
import { BaseProject } from 'src/app/model/project/base-project';
import { Repo } from 'src/app/model/project/repo';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import EmployeeHelper from '../../../../../helpers/employee-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { NonImmoBaseProjectExecutionInfoControllerRequestService } from 'src/app/services/api/non-immo-base-project-execution-info-controller-request.service';
import {
  ImmoExecutionInfo,
  ImmoExecutionInfoControllerRequestService,
} from 'src/app/services/api/immo-execution-info/immo-execution-info-controller-request.service';
import { Tour } from 'src/app/model/tour/tour';
import RegisteredAction from 'src/app/model/registered-action';
import { FileHelper } from '../../../../../helpers/file-helper';

@Component({
  selector: 'client-adjustment-summary',
  templateUrl: './client-adjustment-summary.component.html',
  styleUrls: ['./client-adjustment-summary.component.scss'],
})
export class ClientAdjustmentSummaryComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  originalProjectId?: number;

  @Input()
  products: Product[] = [];

  @Input()
  address: Address;

  @Input()
  placementFloor: string;

  @Input()
  executionAssets: ProductAsset[] = [];

  @Input()
  secondBoard: SecondBoard;

  @Input()
  otherOrderPlacementInfo: string;

  @Input()
  adjustment?: Adjustment | Repo | SoldRented | BaseProject;

  @Input()
  form: boolean = false;

  @Input()
  executed: boolean = false;

  @Input()
  baseProjectTour?: Tour;

  @Input()
  baseProjectDoneRegistration?: RegisteredAction;

  title: string;

  arrowCount: number = 0;
  extrasString: string;
  projectHasIPBoards: boolean = false;

  originalPlacementDate: Date;
  originalPlacers: string;
  isImage = FileHelper.isImage;

  constructor(
    private _lightbox: Lightbox,
    private immoExecutionInfoControllerRequestService: ImmoExecutionInfoControllerRequestService,
    private nonImmoBaseProjectExecutionInfoControllerRequestService: NonImmoBaseProjectExecutionInfoControllerRequestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.calculateProjectHasIPBoards();
    this.handleOriginalPlacementInfo();

    const firstProduct = this.products[0];
    if (ProductHelper.isIPBoard(firstProduct)) {
      if ((<IPBoard>firstProduct).boardPart.sign != null) {
        this.title = 'Voorraadbord';
      } else {
        this.title = 'Custom bord';
      }
    } else if (
      ProductHelper.isWindowSticker(firstProduct) ||
      ProductHelper.isCustomWindowSticker(firstProduct)
    ) {
      this.title = 'Raamsticker';
    } else if (ProductHelper.isBoard(firstProduct)) {
      this.title = 'Projectbord';
    } else {
      this.title = 'Andere';
    }

    this.products.forEach((p: Product) => {
      if (p['arrows'] && p['arrows'] != null)
        this.arrowCount = +(
          p['arrows'].amountArrowsLeft + p['arrows'].amountArrowsRight
        );
    });

    const extras = [];
    if (this.arrowCount > 0) {
      extras.push(
        this.arrowCount + (this.arrowCount === 1 ? ' pijl' : ' pijlen')
      );
    }
    if (this.secondBoard != null) {
      extras.push('2de bord zelfde pand');
    }
    if (
      this.otherOrderPlacementInfo != null &&
      this.otherOrderPlacementInfo != ''
    ) {
      extras.push('Andere bestelling');
    }
    if (extras.length > 0) {
      this.extrasString = extras.join(', ');
    }
  }

  private handleOriginalPlacementInfo(): void {
    if (
      this.originalProjectId == null &&
      (this.baseProjectTour != null || this.baseProjectDoneRegistration != null)
    ) {
      if (this.baseProjectTour != null) {
        this.originalPlacementDate = this.baseProjectTour.date;
        this.originalPlacers = StringHelper.join(
          [
            this.baseProjectTour.placer.user.firstName == null
              ? null
              : this.baseProjectTour.placer.user.firstName.toString(),
            this.baseProjectTour.placer.user.lastName == null
              ? null
              : this.baseProjectTour.placer.user.lastName.toString(),
          ],
          ' '
        );
      } else {
        this.originalPlacementDate = this.baseProjectDoneRegistration.date;
        this.originalPlacers = StringHelper.join(
          [
            this.baseProjectDoneRegistration.user.firstName == null
              ? null
              : this.baseProjectDoneRegistration.user.firstName.toString(),
            this.baseProjectDoneRegistration.user.lastName == null
              ? null
              : this.baseProjectDoneRegistration.user.lastName.toString(),
          ],
          ' '
        );
      }
    } else {
      if (this.projectHasIPBoards) {
        this.getOriginalExecutionInfo();
      } else {
        this.handleNonImmoOriginalPlacementInfo();
      }
    }
  }

  private handleNonImmoOriginalPlacementInfo(): void {
    if (this.originalProjectId != null) {
      this.nonImmoBaseProjectExecutionInfoControllerRequestService
        .nonImmoBaseProjectExecutionInfoForProject(this.originalProjectId)
        .then((executionInfo) => {
          this.originalPlacers = StringHelper.join(
            executionInfo.placerNames,
            ', '
          );
          this.originalPlacementDate = executionInfo.currentExecutionDate;
        });
    }
  }

  private getOriginalExecutionInfo() {
    if (this.originalProjectId != null) {
      this.immoExecutionInfoControllerRequestService
        .getImmoExecutionInfo(this.originalProjectId)
        .then((executionInfo: ImmoExecutionInfo) => {
          if (executionInfo != null) {
            this.originalPlacementDate = executionInfo.date;
            this.originalPlacers =
              executionInfo.placer == null
                ? null
                : EmployeeHelper.getName(executionInfo.placer);
          } else {
            //fallback
            this.handleNonImmoOriginalPlacementInfo();
          }
        });
    }
  }

  private calculateProjectHasIPBoards(): void {
    this.projectHasIPBoards =
      CollectionHelper.isPopulated(this.products) &&
      this.products.some((p) => ProductHelper.isIPBoard(p));
  }

  openLightbox(): void {
    let albums = [];
    for (let asset of this.executionAssets) {
      if (asset.archived) {
        continue;
      }
      const src = '/api/assets/' + asset.asset.uuid;
      const caption = asset.asset.name;
      const thumb = '/api/assets/' + asset.asset.uuid;
      const album = {
        src: src,
        caption: caption,
        thumb: thumb,
      };

      albums.push(album);
    }
    this._lightbox.open(albums, 0);
  }

  getProductSize(product: Product) {
    let sizeArr = [];
    let boardPart = null;
    if (product.type === CLIENT_WINDOW_STICKER_TYPE) {
      sizeArr = product['clientStickerParts'].map((p) => {
        if (p.partType === 1) {
          return `${p.instance.stickerHeight}x${p.instance.stickerWidth}`;
        } else {
          if (p.instance.productSize != null) {
            return `${p.instance.productSize.height}x${p.instance.productSize.width}`;
          }
        }
      });
    } else if (ProductHelper.isWindowSticker(product)) {
      sizeArr = (<Windowsticker>product).standardStickerParts.map((p) => {
        if (p.productSize != null) {
          return `${p.productSize.height}x${p.productSize.width}`;
        }
      });
    } else if (ProductHelper.isCustomWindowSticker(product)) {
      sizeArr = (<CustomWindowsticker>product).stickerParts.map(
        (p) => `${p.stickerHeight}x${p.stickerWidth}`
      );
    } else if (ProductHelper.isBoard(product)) {
      sizeArr = (<Board>product).boardParts.map(
        (b) =>
          `${b.height}x${b.width} / ${b.boardType.label} ${b.placementType.label}`
      );
    } else if (ProductHelper.isIPBoard(product)) {
      boardPart = (<IPBoard>product).boardPart;
      if (boardPart.sign != null) {
        const size =
          boardPart.sign.signMeasurements == null
            ? null
            : `${boardPart.sign.signMeasurements.height}x${boardPart.sign.signMeasurements.width} / ${boardPart.boardType.label} ${boardPart.placementType.label}`;
        sizeArr = [size];
      } else if (
        boardPart.boardType == null ||
        boardPart.boardType.ordinal !== BoardTypeChoices.DIVERGENT.ordinal
      ) {
        sizeArr = [
          `${boardPart.otherHeight}x${boardPart.otherWidth} / ${
            boardPart.boardType.label
          } ${
            boardPart.placementType == null ? '' : boardPart.placementType.label
          }`,
        ];
      } else {
        sizeArr = [
          `${BoardTypeChoices.DIVERGENT.label}: ${boardPart.divergentBoardType}`,
        ];
      }
    } else {
      sizeArr = [
        `${(<OtherProduct>product).height}x${(<OtherProduct>product).width}`,
      ];
    }
    return sizeArr.join('<br>');
  }
}

@Pipe({
  name: 'productTitleSummary',
})
export class ProductTitleSummaryPipe implements PipeTransform {
  public constructor() {}

  transform(product: Product): string {
    let boardPart = null;
    let title = product.title;
    if (ProductHelper.isIPBoard(product)) {
      boardPart = product.boardPart;
      if (boardPart.titleAdjustment != null) {
        title = boardPart.titleAdjustment.title;
      } else if (boardPart.sign != null) {
        title = boardPart.sign.title;
      } else if (boardPart.otherTitle != null) {
        title = boardPart.otherTitle;
      }
    }

    return title;
  }
}
